import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { styled } from 'linaria/react';

/* ---------------- Styles ---------------- */
const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const TooltipTrigger = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  line-height: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;

  &:focus {
    outline: 2px solid rgba(0, 0, 0, 0.6);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const TooltipBubble = styled.div`
  z-index: 1000;
  min-width: 300px;
  max-width: 320px;
  padding: 10px 12px;
  background: #fff;
  color: #111;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  font-size: 0.9rem;
  line-height: 1.35;
  white-space: normal;

  /* Default desktop-läge (absolute) */
  &.desktop {
    position: absolute;
  }

  /* Mobil-läge (fixed, styrs med inline styles) */
  &.mobile {
    position: fixed;
    top: auto;
    left: auto;
    right: auto;
    bottom: auto;
    transform: none;
  }

  /* Placering (y-axel) – pilens standardlägen */
  &.placement-bottom {
    /* y sätts via JS */
  }
  &.placement-top {
    /* y sätts via JS */
  }

  /* Pil (vi positionerar den via CSS-variabeln --arrow-left oavsett läge) */
  &::before {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    background: #fff;
    border-left: 1px solid rgba(0, 0, 0, 0.12);
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    transform: rotate(45deg);
  }

  &.placement-bottom::before {
    top: -6px;
    left: var(--arrow-left, 16px);
  }
  &.placement-top::before {
    bottom: -6px;
    left: var(--arrow-left, 16px);
    transform: rotate(45deg);
  }

  /* Fade-in */
  animation: tooltip-fade-in 120ms ease-out;
  @keyframes tooltip-fade-in {
    from {
      opacity: 0;
      transform: translateY(-2px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

/* ---------------- Component ---------------- */
const MOBILE_MAX = 640; // px
const VIEWPORT_MARGIN = 12; // px – säkerhetsmarginal från kanter
const GAP = 8; // px – avstånd från triggern

const Tooltip = ({
  trigger,
  triggerLabel = 'Info',
  children,
  placement = 'bottom', // 'bottom' | 'top'
  className,
  initialOpen = false,
  onOpenChange
}) => {
  const [open, setOpen] = useState(initialOpen);
  const [styleInline, setStyleInline] = useState(null); // inline styles för position
  const [modeClass, setModeClass] = useState('desktop'); // 'desktop' | 'mobile'

  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const bubbleRef = useRef(null);
  const bubbleId = useRef(
    'tooltip-' +
      Math.random()
        .toString(36)
        .slice(2)
  ).current;

  const isMobile = () =>
    typeof window !== 'undefined' && window.innerWidth <= MOBILE_MAX;

  const setOpenSafe = useCallback(
    next => {
      setOpen(next);
      onOpenChange && onOpenChange(next);
    },
    [onOpenChange] // bara beroende på onOpenChange
  );

  // Stäng vid klick utanför
  useEffect(() => {
    if (!open) return;
    const onDocClick = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target))
        setOpenSafe(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, setOpenSafe]);

  // Stäng på Esc
  useEffect(() => {
    if (!open) return;
    const onKey = e => {
      if (e.key === 'Escape') setOpenSafe(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, setOpenSafe]);

  // Positionering (desktop + mobil) + piljustering
  useLayoutEffect(() => {
    if (!open) return;

    const compute = () => {
      if (!triggerRef.current) return;
      const mobile = isMobile();
      setModeClass(mobile ? 'mobile' : 'desktop');

      const tr = triggerRef.current.getBoundingClientRect();
      const bw = bubbleRef.current?.offsetWidth || 300;
      const bh = bubbleRef.current?.offsetHeight || 0;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // Target (yta) att utgå från: viewportkoordinater
      const triggerCenterX = tr.left + tr.width / 2;

      // Ideal vänster i viewport: centrera bubblan under/över triggern
      let leftVp = triggerCenterX - bw / 2;
      // Klampa inom viewport
      leftVp = Math.max(
        VIEWPORT_MARGIN,
        Math.min(leftVp, vw - VIEWPORT_MARGIN - bw)
      );

      // Pilens vänster i bubblans koordinater (centrera mot triggerns mitt)
      let arrowLeft = triggerCenterX - leftVp - 5; // pil 10px → -5 för mitten
      arrowLeft = Math.max(8, Math.min(arrowLeft, bw - 18)); // lite padding

      if (mobile) {
        // Mobil: fixed, koordinater direkt i viewport
        const topVp =
          placement === 'top'
            ? Math.max(VIEWPORT_MARGIN, tr.top - GAP - bh) // klampa om du vill undvika att tooltip hoppar utanför top
            : Math.min(tr.bottom + GAP, vh - VIEWPORT_MARGIN - bh);
        setStyleInline({
          position: 'fixed',
          top: topVp,
          left: leftVp,
          '--arrow-left': `${Math.round(arrowLeft)}px`
        });
      } else {
        // Desktop: absolute relativt wrapper
        // leftRel = (vänster i viewport) - (wrapperns vänster i viewport)
        const wr = wrapperRef.current?.getBoundingClientRect();
        const leftRel = Math.round(leftVp - (wr?.left ?? 0));

        const topRel =
          placement === 'top'
            ? -(bh + GAP)
            : triggerRef.current.offsetHeight + GAP;

        setStyleInline({
          left: leftRel,
          top: topRel,
          '--arrow-left': `${Math.round(arrowLeft)}px`
        });
      }
    };

    // initial + nästa frame (säkerställa DOM-mått)
    compute();
    const raf = requestAnimationFrame(compute);

    const onScroll = () => compute();
    const onResize = () => compute();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [open, placement]);

  const classNames = `${modeClass} placement-${placement} ${className ??
    ''}`.trim();

  return (
    <TooltipWrapper ref={wrapperRef} className={'tooltip-wrapper'}>
      <TooltipTrigger
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={bubbleId}
        aria-label={triggerLabel}
        onClick={() => setOpenSafe(!open)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpenSafe(!open);
          }
        }}
      >
        {trigger ?? 'ⓘ'}
      </TooltipTrigger>

      {open && (
        <TooltipBubble
          ref={bubbleRef}
          role="dialog"
          id={bubbleId}
          className={classNames}
          style={styleInline || undefined}
        >
          {children}
        </TooltipBubble>
      )}
    </TooltipWrapper>
  );
};

export default Tooltip;
